import {
  forwardRef,
  useCallback,
  useId,
  useRef,
  useState,
  type ChangeEventHandler,
  type DragEventHandler,
  type KeyboardEventHandler,
  type MutableRefObject,
} from 'react';
import {
  IconFile,
  IconFileSpreadsheet,
  IconFileTypeCsv,
  IconFileTypePdf,
  IconPhoto,
  IconPlus,
  IconX,
} from '@tabler/icons-react';
import { cx } from '../utils/cx.js';
import { IconButton } from './IconButton.js';
import { formatFileByteSizeFr } from './FilePickerField.js';
import styles from './DocumentDropzone.module.css';

function FileChipIcon({ file }: { file: File }) {
  const name = file.name.toLowerCase();
  const type = (file.type || '').toLowerCase();
  if (name.endsWith('.pdf') || type === 'application/pdf') {
    return <IconFileTypePdf size={20} stroke={1.6} />;
  }
  if (name.endsWith('.csv') || type === 'text/csv' || type === 'application/csv') {
    return <IconFileTypeCsv size={20} stroke={1.6} />;
  }
  if (
    name.endsWith('.xlsx') ||
    name.endsWith('.xls') ||
    type.includes('spreadsheet') ||
    type.includes('excel')
  ) {
    return <IconFileSpreadsheet size={20} stroke={1.6} />;
  }
  if (type.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg|avif)$/.test(name)) {
    return <IconPhoto size={20} stroke={1.6} />;
  }
  return <IconFile size={20} stroke={1.6} />;
}

export type DocumentDropzoneProps = {
  className?: string;
  accept?: string;
  disabled?: boolean;
  /** Stretch zone to fill a flex parent (stable wizard panels). */
  fill?: boolean;
  /** Shorter drop zone for popovers / image pickers. */
  compact?: boolean;
  /** Selected file shown in the chip under the drop zone. */
  file?: File | null;
  heading?: string;
  hint?: string;
  'aria-label'?: string;
  'aria-invalid'?: boolean;
  onFileChange: (file: File | null) => void;
};

function acceptMatches(file: File, accept: string | undefined): boolean {
  if (!accept?.trim()) return true;
  const tokens = accept.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean);
  if (tokens.length === 0) return true;
  const name = file.name.toLowerCase();
  const type = (file.type || '').toLowerCase();
  return tokens.some((token) => {
    if (token.startsWith('.')) return name.endsWith(token);
    if (token.endsWith('/*')) {
      const prefix = token.slice(0, -1);
      return type.startsWith(prefix);
    }
    return type === token;
  });
}

export const DocumentDropzone = forwardRef<HTMLInputElement, DocumentDropzoneProps>(
  function DocumentDropzone(
    {
      className,
      accept = 'application/pdf,.pdf',
      disabled,
      fill = false,
      compact = false,
      file = null,
      heading = 'Ajouter un document',
      hint = 'Glissez-déposez votre document ici.',
      'aria-label': ariaLabel,
      'aria-invalid': ariaInvalid,
      onFileChange,
    },
    forwardedRef,
  ) {
    const autoId = useId();
    const inputId = `wp-doc-drop-${autoId}`;
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [dragging, setDragging] = useState(false);
    const dragDepth = useRef(0);

    const setInputRef = useCallback(
      (node: HTMLInputElement | null) => {
        inputRef.current = node;
        if (typeof forwardedRef === 'function') forwardedRef(node);
        else if (forwardedRef) {
          (forwardedRef as MutableRefObject<HTMLInputElement | null>).current = node;
        }
      },
      [forwardedRef],
    );

    const openPicker = useCallback(() => {
      if (disabled) return;
      inputRef.current?.click();
    }, [disabled]);

    const applyFile = useCallback(
      (next: File | null) => {
        if (disabled) return;
        onFileChange(next);
      },
      [disabled, onFileChange],
    );

    const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
      const picked = e.currentTarget.files?.[0] ?? null;
      e.currentTarget.value = '';
      if (picked && !acceptMatches(picked, accept)) return;
      applyFile(picked);
    };

    const handleDragEnter: DragEventHandler<HTMLDivElement> = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;
      dragDepth.current += 1;
      setDragging(true);
    };

    const handleDragLeave: DragEventHandler<HTMLDivElement> = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setDragging(false);
    };

    const handleDragOver: DragEventHandler<HTMLDivElement> = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;
      e.dataTransfer.dropEffect = 'copy';
    };

    const handleDrop: DragEventHandler<HTMLDivElement> = (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragDepth.current = 0;
      setDragging(false);
      if (disabled) return;
      const dropped = e.dataTransfer.files?.[0] ?? null;
      if (!dropped) return;
      if (!acceptMatches(dropped, accept)) return;
      applyFile(dropped);
    };

    const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
      if (disabled) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openPicker();
      }
    };

    return (
      <div className={cx(styles.root, fill && styles.rootFill, className)}>
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label={ariaLabel ?? heading}
          aria-disabled={disabled || undefined}
          aria-invalid={ariaInvalid || undefined}
          aria-controls={inputId}
          className={cx(
            styles.zone,
            fill && styles.zoneFill,
            compact && styles.zoneCompact,
            dragging && styles.dragging,
            disabled && styles.disabled,
          )}
          onClick={openPicker}
          onKeyDown={handleKeyDown}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            ref={setInputRef}
            id={inputId}
            type="file"
            className={styles.visuallyHidden}
            accept={accept}
            disabled={disabled}
            tabIndex={-1}
            onChange={handleChange}
          />
          <div className={styles.illustration} aria-hidden>
            <div className={cx(styles.card, styles.cardLeft)}>
              <span className={styles.line} />
              <span className={cx(styles.line, styles.lineShort)} />
              <span className={styles.line} />
            </div>
            <div className={cx(styles.card, styles.cardCenter)}>
              <IconPlus size={28} stroke={2} className={styles.plus} />
            </div>
            <div className={cx(styles.card, styles.cardRight)}>
              <span className={styles.line} />
              <span className={cx(styles.line, styles.lineShort)} />
              <span className={styles.line} />
            </div>
          </div>
          <div className={styles.copy}>
            <p className={styles.heading}>{heading}</p>
            <p className={styles.hint}>{hint}</p>
          </div>
        </div>

        {file ? (
          <div className={styles.fileChip}>
            <span className={styles.fileIcon} aria-hidden>
              <FileChipIcon file={file} />
            </span>
            <div className={styles.fileMeta}>
              <p className={styles.fileName} title={file.name}>
                {file.name}
              </p>
              <p className={styles.fileSize}>{formatFileByteSizeFr(file.size)}</p>
            </div>
            <IconButton
              type="button"
              variant="ghost"
              size="xs"
              disabled={disabled}
              className={styles.clearBtn}
              aria-label="Retirer le fichier"
              onClick={() => applyFile(null)}
            >
              <IconX size={16} stroke={1.75} />
            </IconButton>
          </div>
        ) : null}
      </div>
    );
  },
);
